import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TrendingUp, Users, Wallet, Bell, CheckCircle2, XCircle, IndianRupee, Award, Download } from "lucide-react";
import { AcademicAnalytics } from "./AdminDashboard";
import EventsPanel from "./EventsPanel";
import LoansTransfers from "@/pages/finance/LoansTransfers";
import BankOps from "@/pages/finance/BankOps";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "cashflow", label: "Cash Flow" },
  { id: "donations", label: "Donations" },
  { id: "expenses", label: "Expense Log & Approvals" },
  { id: "payroll_approvals", label: "Payroll Approvals" },
  { id: "loans", label: "Loans & Transfers" },
  { id: "bank", label: "Bank Accounts" },
  { id: "event_approvals", label: "Event Approvals" },
  { id: "acad_analytics", label: "Academic Analytics" },
];

export default function FounderDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  return (
    <Shell title="Founder Console" subtitle={user?.name} tabs={TABS} active={tab} onTab={setTab}>
      {tab === "overview"  && <Overview />}
      {tab === "cashflow"  && <CashFlow />}
      {tab === "donations" && <Donations />}
      {tab === "expenses"  && <Expenses />}
      {tab === "payroll_approvals" && <PayrollApprovals />}
      {tab === "loans" && <LoansTransfers mode="review"/>}
      {tab === "bank" && <BankOps role="founder"/>}
      {tab === "event_approvals" && <EventsPanel mode="approve"/>}
      {tab === "acad_analytics" && <AcademicAnalytics />}
    </Shell>
  );
}

function PayrollApprovals() {
  const [runs, setRuns] = useState([]);
  const [tab, setSubTab] = useState("pending");
  const load = async () => { setRuns((await api.get("/payroll")).data); };
  useEffect(()=>{ load(); }, []);
  const approve = async (pid) => {
    try { await api.post(`/payroll/${pid}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (pid) => {
    const note = window.prompt("Reason for rejection (optional):") || "";
    try { await api.post(`/payroll/${pid}/reject`, { note }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const download = async (pid, name, month) => {
    try {
      const res = await api.get(`/payroll/${pid}/payslip-pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `payslip_${(name||"staff").replace(/\s+/g,"_")}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("PDF unavailable"); }
  };
  const filtered = runs.filter(r => {
    if (tab==="pending") return r.status === "pending_approval";
    if (tab==="approved") return r.status === "approved";
    if (tab==="paid") return r.status === "paid";
    return true;
  });
  return (
    <div className="space-y-4" data-testid="f-payroll-approvals">
      <div className="flex gap-2 flex-wrap">
        {[["pending","Pending Approval"],["approved","Approved"],["paid","Paid"],["all","All"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)}
                  className={`nb-btn ${tab===id?"":"nb-btn-ghost"}`}
                  data-testid={`f-pay-tab-${id}`}>{label}</button>
        ))}
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Staff</th><th>Month</th><th>Gross</th><th>Deductions</th><th>Net</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.payroll_id} data-testid={`f-pay-row-${r.payroll_id}`}>
                <td>{r.beneficiary_name || r.educator_name}</td>
                <td className="mono">{r.month}</td>
                <td className="mono">₹{(r.gross||0).toLocaleString()}</td>
                <td className="mono">₹{(r.total_deductions||0).toLocaleString()}</td>
                <td className="mono font-bold">₹{(r.net||0).toLocaleString()}</td>
                <td><span className="nb-badge">{(r.status||"").toUpperCase()}</span></td>
                <td className="space-x-1 whitespace-nowrap">
                  {r.status === "pending_approval" && (
                    <>
                      <button onClick={()=>approve(r.payroll_id)}
                              className="nb-btn text-xs" data-testid={`f-pay-approve-${r.payroll_id}`}>Approve</button>
                      <button onClick={()=>reject(r.payroll_id)}
                              className="nb-btn nb-btn-danger text-xs" data-testid={`f-pay-reject-${r.payroll_id}`}>Reject</button>
                    </>
                  )}
                  {(r.status==="approved"||r.status==="paid") && (
                    <button onClick={()=>download(r.payroll_id, r.beneficiary_name || r.educator_name, r.month)}
                            className="nb-btn nb-btn-secondary text-xs"
                            data-testid={`f-pay-dl-${r.payroll_id}`}>
                      <Download size={12}/> PDF
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No records</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [s, setS] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0,7));
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)).catch(() => setS({})); }, []);
  useEffect(() => {
    api.get("/analytics/attendance-summary", { params: { month }})
      .then(r => setAnalytics(r.data)).catch(() => setAnalytics({month, grades: []}));
  }, [month]);
  const shiftMonth = (delta) => {
    const d = new Date(month + "-01");
    d.setMonth(d.getMonth() + delta);
    setMonth(d.toISOString().slice(0,7));
  };
  const monthLabel = new Date(month + "-01").toLocaleString("en-US",{month:"short", year:"numeric"}).replace(" ","");
  if (!s) return <div className="mono p-4">Loading…</div>;
  const cards = [
    { label:"Students",           value: s.students ?? 0,                    bg:"bg-[#93C5FD]", Icon:Users },
    { label:"Educators",          value: s.educators ?? 0,                   bg:"bg-[#86EFAC]", Icon:Users },
    { label:"Total Revenue (₹)",  value: Math.round(s.revenue ?? 0).toLocaleString(), bg:"bg-[#C4B5FD]", Icon:Wallet },
    { label:"Pending Invoices",   value: s.pending_invoices ?? 0,            bg:"bg-[#FDE047]", Icon:IndianRupee },
    { label:"Paid Invoices",      value: s.paid_invoices ?? 0,               bg:"bg-[#86EFAC]", Icon:CheckCircle2 },
    { label:"New Enquiries",      value: s.new_enquiries ?? 0,               bg:"bg-[#FCA5A5]", Icon:Bell },
  ];
  const pctColour = (p) => p >= 90 ? "text-emerald-700" : p >= 75 ? "text-amber-600" : "text-rose-700";
  const gradeCardBg = (idx) => ["bg-[#FDE047]","bg-[#93C5FD]","bg-[#C4B5FD]","bg-[#86EFAC]","bg-[#FCA5A5]"][idx % 5];
  return (
    <div className="space-y-6">
      <div className="nb-card p-6 bg-[#FDE047]">
        <div className="flex items-center gap-3">
          <Award size={28} />
          <div>
            <div className="display text-2xl font-bold">Welcome, Founder</div>
            <p className="text-sm mt-1">
              Read-only oversight with expense-approval authority. Review cash flow,
              donations, and monitor grade-wise attendance trends below.
            </p>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map(c => (
          <div key={c.label} className={`nb-card p-5 ${c.bg}`}
               data-testid={`f-stat-${c.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <c.Icon size={20} />
            <div className="nb-label mt-2">{c.label}</div>
            <div className="display text-4xl font-black">{c.value}</div>
          </div>
        ))}
      </div>

      {/* ---- Attendance Analytics ---- */}
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={20}/>
            <h3 className="display text-xl font-bold">Attendance Analytics — {monthLabel}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>shiftMonth(-1)} className="nb-btn nb-btn-ghost px-2 py-1" data-testid="f-att-month-prev">◀</button>
            <span className="mono font-bold text-sm" data-testid="f-att-month-label">{monthLabel}</span>
            <button onClick={()=>shiftMonth(1)} className="nb-btn nb-btn-ghost px-2 py-1" data-testid="f-att-month-next">▶</button>
          </div>
        </div>
        {!analytics ? <div className="p-6 mono text-sm">Loading…</div> :
          analytics.grades.length === 0 ? (
            <div className="p-6 text-slate-500 text-sm">No student data yet for {monthLabel}.</div>
          ) : (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {analytics.grades.map((g, i) => (
                <div key={g.grade} className={`nb-card p-4 ${gradeCardBg(i)}`}
                     data-testid={`f-att-card-${g.grade}`}>
                  <div className="flex justify-between items-baseline">
                    <div className="display text-2xl font-bold">Grade {g.grade}</div>
                    <div className="text-xs mono opacity-70">{g.student_count} students · {g.staff_count} educators</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <div className="nb-label text-[10px]">Student Attendance</div>
                      <div className={`display text-3xl font-black mono ${pctColour(g.student_att_pct)}`}
                           data-testid={`f-att-stu-pct-${g.grade}`}>{g.student_att_pct}%</div>
                      <div className="text-[10px] mono opacity-70">{g.student_marked_days} student-days marked</div>
                    </div>
                    <div>
                      <div className="nb-label text-[10px]">Staff Attendance</div>
                      <div className={`display text-3xl font-black mono ${pctColour(g.staff_att_pct)}`}
                           data-testid={`f-att-staff-pct-${g.grade}`}>{g.staff_att_pct}%</div>
                      <div className="text-[10px] mono opacity-70">{g.staff_marked_days} staff-days marked</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

/* ---------------- Cash In Ledger ---------------- */
function CashFlow() {
  const [data, setData] = useState({inflows:{fees:0,donations:0,total:0}, outflows:{expenses:0,payroll:0,total:0}, net:0, rows:[]});
  const [feeType, setFeeType] = useState("");
  const [feeTypes, setFeeTypes] = useState([]);
  const [fromDate, setFrom] = useState("");
  const [toDate, setTo] = useState("");
  // Iter24
  const [month, setMonth] = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [includePayroll, setIncludePayroll] = useState(true);
  // Iter28 — Ledger record picker (direction auto-derived, no manual dropdown)
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const load = useCallback(async () => {
    const params = {};
    if (feeType) params.fee_type = feeType;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    const r = await api.get("/cashflow", { params });
    setData(r.data);
  }, [feeType, fromDate, toDate]);
  useEffect(() => {
    api.get("/settings").then(r=>setFeeTypes(r.data.fee_types||[])).catch(()=>{});
    api.get("/finance-settings/divisions").then(r=>setDivisions(r.data)).catch(()=>{});
    api.get("/finance-settings/ledger-types").then(r=>setLedgers(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); }, [load]);
  const printSummary = async () => {
    try {
      const res = await api.post("/cashflow/print-summary",
        {month: month || null, division_id: divisionId || null,
         ledger_id: ledgerId || null,
         include_payroll: includePayroll},
        {responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cashflow_${(month||'all')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const TYPE_STYLE = { FEE: "bg-[#86EFAC]", DONATION: "bg-[#FDE047]", EXPENSE: "bg-[#FCA5A5]", PAYROLL: "bg-[#C4B5FD]" };
  const selLedger = ledgers.find(l => l.ledger_id === ledgerId);
  let inflowRows = data.rows.filter(r => r.type === "FEE" || r.type === "DONATION");
  let outflowRows = data.rows.filter(r => r.type === "EXPENSE" || r.type === "PAYROLL");
  if (selLedger) {
    const ins = new Set(selLedger.cash_in_types || []);
    const outs = new Set(selLedger.cash_out_types || []);
    const staffIds = new Set(selLedger.payroll_staff_ids || []);
    const hasIn = ins.size > 0;
    const hasOut = outs.size > 0 || staffIds.size > 0;
    if (hasIn) {
      inflowRows = inflowRows.filter(r => {
        if (r.type === "DONATION") return ins.has("Donations");
        const keys = Object.keys(r.breakdown || {});
        return keys.some(k => ins.has(k));
      });
    }
    if (hasOut) {
      outflowRows = outflowRows.filter(r => {
        if (r.type === "EXPENSE") return outs.has(r.category);
        if (staffIds.size > 0) return staffIds.has(r.beneficiary_id);
        return outs.has("Monthly Payroll") || outs.has("Payroll");
      });
    }
    if (hasIn && !hasOut) outflowRows = [];
    if (hasOut && !hasIn) inflowRows = [];
  }
  return (
    <div className="space-y-6">
      <div className="nb-card p-5 bg-[#FDE047] flex flex-wrap gap-3 items-end" data-testid="f-cf-filter-bar">
        <div><label className="nb-label">Ledger / Cash Account</label>
          <select className="nb-select mt-1" value={ledgerId}
                  onChange={e=>setLedgerId(e.target.value)}
                  data-testid="f-cf-ledger-id">
            <option value="">All ledgers</option>
            {ledgers.map(l => {
              const badge = l.cash_in_types?.length && (l.cash_out_types?.length || l.payroll_staff_ids?.length)
                ? "Both" : l.cash_in_types?.length ? "Cash-In" : "Cash-Out";
              return <option key={l.ledger_id} value={l.ledger_id}>{l.name} · {badge}</option>;
            })}
          </select></div>
        <div><label className="nb-label">Fee Type</label>
          <select data-testid="f-cf-fee-type" className="nb-select mt-1"
                  value={feeType} onChange={e=>setFeeType(e.target.value)}>
            <option value="">All</option>
            {feeTypes.map(t=><option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label className="nb-label">Month</label>
          <input type="month" className="nb-input mt-1" value={month}
                 onChange={e=>setMonth(e.target.value)} data-testid="f-cf-month"/></div>
        <div><label className="nb-label">Division</label>
          <select className="nb-select mt-1" value={divisionId}
                  onChange={e=>setDivisionId(e.target.value)}
                  data-testid="f-cf-division">
            <option value="">All divisions</option>
            {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
          </select></div>
        <div><label className="nb-label">From</label>
          <input type="date" className="nb-input mt-1" value={fromDate}
                 onChange={e=>setFrom(e.target.value)} data-testid="f-cf-from"/></div>
        <div><label className="nb-label">To</label>
          <input type="date" className="nb-input mt-1" value={toDate}
                 onChange={e=>setTo(e.target.value)} data-testid="f-cf-to"/></div>
        <label className="flex items-center gap-2 text-xs pb-2">
          <input type="checkbox" checked={includePayroll}
                 onChange={e=>setIncludePayroll(e.target.checked)}
                 data-testid="f-cf-include-payroll"/>
          Include approved payroll (Cash-Out)
        </label>
        <button className="nb-btn ml-auto" onClick={printSummary}
                data-testid="f-cf-print-summary"><Download size={14}/>Print Summary</button>
        <div className="w-full text-right">
          <div className="nb-label">Net Cash Flow</div>
          <div className={`display text-3xl font-black ${data.net>=0?"text-emerald-700":"text-rose-700"}`}
               data-testid="f-cf-net">₹{(data.net||0).toLocaleString()}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="nb-card-sm p-3 bg-[#86EFAC]"><div className="nb-label text-[10px]">Fees (Inflow)</div>
          <div className="font-bold mono" data-testid="f-cf-fees">₹{data.inflows.fees.toLocaleString()}</div></div>
        <div className="nb-card-sm p-3 bg-[#FDE047]"><div className="nb-label text-[10px]">Donations (Inflow)</div>
          <div className="font-bold mono" data-testid="f-cf-donations">₹{data.inflows.donations.toLocaleString()}</div></div>
        <div className="nb-card-sm p-3 bg-[#FCA5A5]"><div className="nb-label text-[10px]">Expenses (Outflow)</div>
          <div className="font-bold mono" data-testid="f-cf-expenses">₹{data.outflows.expenses.toLocaleString()}</div></div>
        <div className="nb-card-sm p-3 bg-[#C4B5FD]"><div className="nb-label text-[10px]">Payroll (Outflow)</div>
          <div className="font-bold mono" data-testid="f-cf-payroll">₹{data.outflows.payroll.toLocaleString()}</div></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-0 overflow-hidden">
          <div className="px-6 py-3 border-b-2 border-black bg-[#86EFAC] flex justify-between items-center">
            <h3 className="display text-lg font-bold">Inflows ({inflowRows.length})</h3>
            <span className="mono font-bold">₹{data.inflows.total.toLocaleString()}</span>
          </div>
          <div className="max-h-[45vh] overflow-auto">
            <table className="nb-table">
              <thead><tr><th>Date</th><th>Type</th><th>Party</th><th>Division</th><th>Ref</th><th>Amount</th></tr></thead>
              <tbody>
                {inflowRows.map((r,i)=>(
                  <tr key={`${r.ref}-${i}`}>
                    <td className="mono text-xs">{r.date}</td>
                    <td><span className={`nb-badge ${TYPE_STYLE[r.type]}`}>{r.type}</span></td>
                    <td className="font-bold">{r.party}</td>
                    <td className="text-xs">{r.division || "—"}</td>
                    <td className="mono text-xs">{r.ref}</td>
                    <td className="mono font-bold">₹{r.amount.toLocaleString()}</td>
                  </tr>
                ))}
                {inflowRows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No inflows in range</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="nb-card p-0 overflow-hidden">
          <div className="px-6 py-3 border-b-2 border-black bg-[#FCA5A5] flex justify-between items-center">
            <h3 className="display text-lg font-bold">Outflows ({outflowRows.length})</h3>
            <span className="mono font-bold">₹{data.outflows.total.toLocaleString()}</span>
          </div>
          <div className="max-h-[45vh] overflow-auto">
            <table className="nb-table">
              <thead><tr><th>Date</th><th>Type</th><th>Party</th><th>Division</th><th>Ref</th><th>Amount</th></tr></thead>
              <tbody>
                {outflowRows.map((r,i)=>(
                  <tr key={`${r.ref}-${i}`}>
                    <td className="mono text-xs">{r.date}</td>
                    <td><span className={`nb-badge ${TYPE_STYLE[r.type]}`}>{r.type}</span></td>
                    <td className="font-bold">{r.party}</td>
                    <td className="text-xs">{r.division || "—"}</td>
                    <td className="mono text-xs">{r.ref}</td>
                    <td className="mono font-bold">₹{r.amount.toLocaleString()}</td>
                  </tr>
                ))}
                {outflowRows.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No outflows in range</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Donations ---------------- */
function Donations() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/donations").then(r => setRows(r.data)); }, []);
  const total = rows.reduce((a,d) => a + Number(d.amount || 0), 0);
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#86EFAC] flex items-center justify-between">
        <div>
          <div className="nb-label">Total Donations Received</div>
          <div className="display text-3xl font-black mono" data-testid="f-donations-total">
            ₹{total.toLocaleString()}
          </div>
        </div>
        <div className="text-sm text-right">
          <div className="font-bold">{rows.length}</div>
          <div>total donors</div>
        </div>
      </div>
      <div className="nb-card p-0 overflow-x-auto">
        <table className="nb-table">
          <thead>
            <tr>
              <th>Date</th><th>Donor</th><th>Mobile</th><th>Division</th>
              <th>Purpose</th><th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.donation_id} data-testid={`f-donation-${d.donation_id}`}>
                <td className="mono text-xs">{(d.donated_at || d.created_at || "").slice(0,10)}</td>
                <td className="font-bold">{d.donor_name}</td>
                <td className="text-xs mono">{d.mobile_number || d.pan || d.donor_mobile || "—"}</td>
                <td className="text-xs">{d.division_name || "—"}</td>
                <td className="text-sm">{d.purpose || d.message || "General"}</td>
                <td className="text-right mono font-bold">₹{Number(d.amount).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500">No donations yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Expenses + Approvals (moved from Admin) ---------------- */
function Expenses() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(() => api.get("/expenses").then(r => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, action) => {
    try {
      await api.post(`/expenses/${id}/decision`, { action });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const filtered = rows.filter(r => filter === "all" ? true : r.status === filter);
  const counts = {
    pending: rows.filter(r=>r.status==="pending").length,
    approved: rows.filter(r=>r.status==="approved").length,
    rejected: rows.filter(r=>r.status==="rejected").length,
  };
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#FCA5A5]">
        <div className="display text-xl font-bold">Expense Approvals</div>
        <p className="text-sm mt-1">
          As Founder, you have the authority to approve or reject school expenses.
          Approvals are logged with your user ID and timestamp.
        </p>
        <div className="flex gap-2 mt-3 flex-wrap">
          {[
            { id: "pending",  label: `Pending (${counts.pending})`,   bg:"bg-[#FDE047]" },
            { id: "approved", label: `Approved (${counts.approved})`, bg:"bg-[#86EFAC]" },
            { id: "rejected", label: `Rejected (${counts.rejected})`, bg:"bg-[#FCA5A5]" },
            { id: "all",      label: `All (${rows.length})`,          bg:"bg-white" },
          ].map(t => (
            <button key={t.id} onClick={()=>setFilter(t.id)}
                    data-testid={`f-exp-filter-${t.id}`}
                    className={`nb-btn ${filter===t.id ? "" : "nb-btn-ghost"} ${t.bg}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="nb-card p-0 overflow-x-auto">
        <table className="nb-table">
          <thead>
            <tr>
              <th>Date</th><th>Category</th><th>Description</th>
              <th className="text-right">Amount</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.expense_id} data-testid={`f-expense-${e.expense_id}`}>
                <td className="mono text-xs">{(e.spent_at || e.created_at || "").slice(0,10)}</td>
                <td><span className="nb-badge bg-white">{e.category}</span></td>
                <td className="text-sm">{e.description}</td>
                <td className="text-right mono font-bold">₹{Number(e.amount).toLocaleString()}</td>
                <td>
                  <span className={`nb-badge ${
                    e.status==="approved" ? "bg-[#86EFAC]" :
                    e.status==="rejected" ? "bg-[#FCA5A5]" : "bg-[#FDE047]"}`}>
                    {e.status}
                  </span>
                </td>
                <td>
                  {e.status === "pending" ? (
                    <div className="flex gap-1">
                      <button onClick={()=>decide(e.expense_id,"approve")}
                              className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                              data-testid={`f-approve-${e.expense_id}`}>
                        <CheckCircle2 size={12}/> Approve
                      </button>
                      <button onClick={()=>decide(e.expense_id,"reject")}
                              className="nb-btn nb-btn-danger px-2 py-1 text-xs"
                              data-testid={`f-reject-${e.expense_id}`}>
                        <XCircle size={12}/> Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500 mono">
                      by {e.decided_by_role || "admin"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500">
                No {filter === "all" ? "" : filter} expenses
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
